import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faArrowLeft } from '@/fontawesome';
import Layout from '@/components/layout/Layout';
import type {FeatureConfigKey} from "@/models";

interface FeatureLockedPageProps {
  featureName: FeatureConfigKey|null;
  featureDescription?: string;
  onLogout: () => void;
}

const FEATURE_NAMES: Record<FeatureConfigKey, string> = {
  coreRecommendations:       'Kernstrategie',
  earningRecommendations:    'Earnings Trades',
  swingTradeRecommendations: 'Swing Trades',
  longTermRecommendations:   'Langzeit Trades',
  chatbot:                   'Trading-Buddy',
  videoChatbot:              'Video-Chatbot',
  journal:                   'Trading-Journal',
  dayTrading:                'Daytrading',
  optionScreener:            'Options-Screener',
  videoSearch:               'Video-Finder',
  boostRecommendations:      'Trading-Boost',
  communityMember:           'Community-Mitglied',
  allCourses:                'Alle Kurse',
};

const FEATURE_DESCRIPTIONS: Record<FeatureConfigKey, string> = {
  coreRecommendations:       'Erhalten Sie Zugriff auf unsere Kernstrategie-Tradingideen für erfolgreiches Trading.',
  earningRecommendations:    'Profitieren Sie von Earnings-Tradingideen basierend auf Unternehmensberichten.',
  swingTradeRecommendations: 'Nutzen Sie unsere Swing-Trade-Strategien für mittelfristige Investments.',
  longTermRecommendations:   'Erhalten Sie Zugriff auf langfristige Investment-Tradingideen.',
  chatbot:                   'Nutzen Sie unseren KI-gestützten Chatbot für individuelle Trading-Analysen.',
  videoChatbot:              'Chatten Sie direkt zu Videos mit unserem intelligenten Video-Assistenten.',
  journal:                   'Dokumentieren und analysieren Sie Ihre Trades mit unserem professionellen Journal.',
  dayTrading:                'Erhalten Sie Zugriff auf Live Daytrading Sessions.',
  optionScreener:            'Durchsuchen und filtern Sie verfügbare Optionen mit dem Options-Screener.',
  videoSearch:               'Finden Sie per KI-Suche das passende Video aus allen freigeschalteten Kursen.',
  boostRecommendations:      'Erhalten Sie Zugriff exklusive Boost Signale',
  communityMember:           'Erhalte Zugriff auf die Trading-Community',
  allCourses:                'Erhalte Zugriff auf alle Kurse',
};

const FEATURE_UNLOCKABLE: Record<FeatureConfigKey, boolean> = {
  coreRecommendations:         true,
  earningRecommendations:      true,
  swingTradeRecommendations:   true,
  longTermRecommendations:     true,
  chatbot:                     false,
  videoChatbot:                false,
  journal:                     false,
  dayTrading:                  false,
  optionScreener:              false,
  videoSearch:                 false,
  boostRecommendations:        false,
  communityMember:             false,
  allCourses:                  false,
};

export default function FeatureLockedPage({ featureName, featureDescription, onLogout }: FeatureLockedPageProps) {

  if (featureName === null) {
    return (
      <Layout onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="max-w-md w-full">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                  <FontAwesomeIcon icon={faLock} className="w-8 h-8 text-gray-600 dark:text-gray-400"/>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Feature gesperrt</h1>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const displayName = FEATURE_NAMES[featureName] || featureName;
  const description = featureDescription || FEATURE_DESCRIPTIONS[featureName] || 'Dieses Feature ist in Ihrem aktuellen Plan nicht enthalten.';
  const unlockable = FEATURE_UNLOCKABLE[featureName] || false;

  return (
    <Layout onLogout={onLogout}>
      <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 text-center">
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                <FontAwesomeIcon icon={faLock} className="w-8 h-8 text-gray-600 dark:text-gray-400"/>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Feature gesperrt</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">{displayName}</p>
            </div>

            <div className="mb-8">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{description}</p>
            </div>

            {unlockable && (
              <div className="space-y-3">
                <Link
                  to="/pricing"
                  className="block w-full px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors text-center"
                >Feature freischalten</Link>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <Link to="/dashboard" className="inline-flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />Zurück zum Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
